import { ComponentType, memo, ReactNode, useCallback, useContext, useMemo } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { getPortalComponents } from "../../../../generic";
import { Breadcrumbs, BreadcrumbsContext } from "../../../Breadcrumbs";
import { Button } from "../../../Button";

import { ReactComponent as Arrow } from "./icons/arrow-left.svg";

import styles from "./style.module.scss";

const { Container, PortalContentProvider } = getPortalComponents();

export const PageLayoutHeaderButtonsContainer = Container;
export const PageLayoutHeaderButtons = PortalContentProvider;

export type PageLayoutHeaderProps = {
  header?: ReactNode,
  children?: ReactNode,
  buttons?: ReactNode,
  hideBackButton?: boolean,
  hideBreadcrumbs?: boolean,
  className?: string,
  headerClassName?: string,
}

export const PageLayoutHeader = memo<PageLayoutHeaderProps>((
  {
    hideBackButton,
    hideBreadcrumbs,
    header,
    children,
    buttons,
    className,
    headerClassName,
  }) => {
  const navigate = useNavigate();
  const breadcrumbs = useContext(BreadcrumbsContext);
  const previousNode = useMemo(
    () => [ ...breadcrumbs ].reverse()
      .find(x => !x.disabled && (x.to || x.href))
    , [ breadcrumbs ]);
  const onBackClick = useCallback(() => {
    if (previousNode?.to) {
      navigate(previousNode.to);
    } else if (previousNode?.href) {
      window.location.href = previousNode.href;
    }
  }, [ previousNode, navigate ]);

  const isBackButtonShown = !hideBackButton && !!previousNode;

  return (
    <div
      className={classNames(styles.root, className)}
      data-page-header-back-button={isBackButtonShown}
    >
      <div className={styles.breadcrumbsContainer}>
        {
          isBackButtonShown &&
          <Button
            className={styles.backButton}
            type="button"
            buttonType="round"
            buttonColorSchema="outlined-light"
            onClick={onBackClick}
          >
            <Arrow width={18} />
          </Button>
        }
        {
          !hideBreadcrumbs && <Breadcrumbs className={styles.breadcrumbs} />
        }
      </div>
      <Container className={styles.buttons} />
      <div className={styles.buttons}>
        {buttons}
      </div>

      <div className={classNames(styles.header, headerClassName)}>
        {
          typeof header === "string"
            ? <h1>{header}</h1>
            : header
        }
        {children}
      </div>
    </div>
  );
});

(PageLayoutHeader as ComponentType<PageLayoutHeaderProps>).defaultProps = {
  hideBackButton: false,
};

export default PageLayoutHeader;
